import type {AppRouteDefinition, AppRouteLayout, RouteBuildContext} from '@router';
import type {ReactNode} from 'react';

import {
  initialHeaderData,
  protectedRoutes,
  publicRoutes,
  resolveLayout,
  RouteBuildProvider
} from '@router';
import {useCallback, useLayoutEffect, useMemo, useState} from 'react';
import {BrowserRouter, Route, Routes, useLocation} from 'react-router-dom';
import ProtectedRoute from 'routes/ProtectedRoute';
import Layout from 'shell/layout/Layout';
import {useAppSelector} from 'store';
import {QueryParamProvider} from 'use-query-params';
import {ReactRouter6Adapter} from 'use-query-params/adapters/react-router-6';

import NotificationHandler from 'utils/NotificationHandler';

function ProtectedAppRoute({
  route,
  ctx,
  resetHeaderData
}: {
  route: AppRouteDefinition;
  ctx: RouteBuildContext;
  resetHeaderData: RouteBuildContext['resetHeaderData'];
}) {
  const location = useLocation();
  const layout: AppRouteLayout = resolveLayout(route.layout, {
    ...ctx,
    location: {pathname: location.pathname, search: location.search}
  });

  useLayoutEffect(() => {
    const key = route.layout.headerDataKey;
    if (key) {
      resetHeaderData(key);
    }
  }, [route.path, route.layout.headerDataKey, resetHeaderData]);

  return (
    <ProtectedRoute>
      <Layout
        linkWithConfirmation={layout.linkWithConfirmation}
        hasNavigate={layout.hasNavigate}
        hasHeader={layout.hasHeader}
        title={layout.title}
        hasBack={layout.hasBack}
        link={layout.link}
        modalTitle={layout.modalTitle}
        modalText={layout.modalText}
        hasLeftIcon={layout.hasLeftIcon}
        leftIconLink={layout.leftIconLink}
        headerData={layout.headerData}
        extra={layout.extra}
      >
        {route.element}
      </Layout>
    </ProtectedRoute>
  );
}

const Routers = () => {
  const [headerData, setHeaderData] = useState(initialHeaderData);
  const updateHeaderData = useCallback<RouteBuildContext['updateHeaderData']>((key, value) => {
    setHeaderData((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, key)) {
        return prev;
      }

      return {...prev, [key]: {...prev[key], ...value}};
    });
  }, []);

  const resetHeaderData = useCallback<RouteBuildContext['resetHeaderData']>((key) => {
    setHeaderData((prev) => {
      if (!Object.prototype.hasOwnProperty.call(initialHeaderData, key)) {
        return prev;
      }

      return {...prev, [key]: {...initialHeaderData[key]}};
    });
  }, []);

  const {selectedVendor} = useAppSelector((state) => state.dashboard);
  const ctx = useMemo<RouteBuildContext>(
    () => ({headerData, updateHeaderData, resetHeaderData, selectedVendor}),
    [headerData, updateHeaderData, resetHeaderData, selectedVendor]
  );

  return (
    <BrowserRouter>
      <NotificationHandler />
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <RouteBuildProvider value={ctx}>
          <Routes>
            {publicRoutes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element as ReactNode} />
            ))}
            {protectedRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ProtectedAppRoute route={route} ctx={ctx} resetHeaderData={resetHeaderData} />
                }
              />
            ))}
          </Routes>
        </RouteBuildProvider>
      </QueryParamProvider>
    </BrowserRouter>
  );
};

export default Routers;
